import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { DATA_AS_OF } from '../../../constants/marketData';

const MarketIntelligence = () => {
  const [activeTab, setActiveTab] = useState('regulatory');
  const [currentUpdate, setCurrentUpdate] = useState(0);

  const tabs = [
    { id: 'regulatory', label: 'Regulatory Updates', icon: 'FileText' },
    { id: 'talent', label: 'Talent Trends', icon: 'Users' },
    { id: 'market', label: 'Market Insights', icon: 'TrendingUp' }
  ];

  const regulatoryUpdates = [
    {
      id: 1,
      title: 'New IT Policy Framework 2024',
      description: 'Government announces enhanced incentives for GCC establishments with focus on emerging technologies and R&D activities.',
      date: '2025-07-14',
      impact: 'High',
      category: 'Policy',
      source: 'Ministry of Electronics & IT'
    },
    {
      id: 2,
      title: 'SEZ Amendment Guidelines',
      description: 'Updated Special Economic Zone regulations streamline approval processes for technology centers and reduce compliance overhead.',
      date: '2025-07-12',
      impact: 'Medium',
      category: 'Compliance',
      source: 'Department of Commerce'
    },
    {
      id: 3,
      title: 'Data Localization Requirements',
      description: 'Clarifications on data residency requirements for financial services and healthcare sectors operating through GCCs.',
      date: '2025-07-10',
      impact: 'High',
      category: 'Data Privacy',
      source: 'RBI & SEBI'
    }
  ];

  const talentTrends = [
    {
      id: 1,
      title: 'AI/ML Talent Surge in Bangalore',
      description: 'Artificial Intelligence and Machine Learning professionals increased by 45% in Q2 2024, with average salary growth of 28%.',
      date: '2025-07-13',
      impact: 'High',
      category: 'Skills',
      metric: '+45%'
    },
    {
      id: 2,
      title: 'Cloud Architecture Specialists',
      description: 'High demand for cloud architects and DevOps engineers across Hyderabad and Pune, with 60% year-over-year growth.',
      date: '2025-07-11',
      impact: 'Medium',
      category: 'Technology',
      metric: '+60%'
    },
    {
      id: 3,
      title: 'Cybersecurity Expertise Gap',
      description: 'Critical shortage of cybersecurity professionals across all major GCC locations, creating competitive hiring landscape.',
      date: '2025-07-09',
      impact: 'High',
      category: 'Security',
      metric: '-25%'
    }
  ];

  const marketInsights = [
    {
      id: 1,
      title: 'GCC Investment Reaches $4.2B',
      description: 'Foreign direct investment in Global Capability Centers hits record high, with 78% focused on technology and innovation.',
      date: '2025-07-15',
      impact: 'High',
      category: 'Investment',
      metric: '$4.2B'
    },
    {
      id: 2,
      title: 'Tier-2 Cities Gaining Momentum',
      description: 'Emerging locations like Kochi, Indore, and Coimbatore see 35% increase in GCC interest due to cost advantages.',
      date: '2025-07-13',
      impact: 'Medium',
      category: 'Geography',
      metric: '+35%'
    },
    {
      id: 3,
      title: 'Sustainability Focus Drives Decisions',
      description: 'Environmental considerations now influence 67% of GCC location decisions, with green building certifications becoming standard.',
      date: '2025-07-11',
      impact: 'Medium',
      category: 'ESG',
      metric: '67%'
    }
  ];

  const liveMetrics = [
    { label: 'Active GCC Projects', value: '1,247', change: '+12%', trend: 'up' },
    { label: 'Avg. Time to Full Scale', value: '8.5 months', change: '-15%', trend: 'down' },
    { label: 'Cost Advantage', value: '58%', change: '+3%', trend: 'up' },
    { label: 'Talent Availability', value: '2.8M+', change: '+8%', trend: 'up' }
  ];

  const getCurrentData = () => {
    switch (activeTab) {
      case 'regulatory':
        return regulatoryUpdates;
      case 'talent':
        return talentTrends;
      case 'market':
        return marketInsights;
      default:
        return regulatoryUpdates;
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High':
        return 'text-error bg-error/10 border-error/20';
      case 'Medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'Low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-text-secondary bg-muted border-border';
    }
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 'TrendingUp' : 'TrendingDown';
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-success' : 'text-error';
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentUpdate((prev) => (prev + 1) % getCurrentData().length);
    }, 4000);

    return () => clearInterval(interval);
  }, [activeTab]);

  return (
    <section className="py-20 bg-background-alt">
      <div className="container-executive">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-body-medium mb-6">
            <Icon name="Activity" size={16} />
            <span>Market Intelligence</span>
          </div>
          
          <h2 className="font-headline font-headline-bold text-3xl lg:text-4xl text-text-primary mb-6">
            AI Intelligence Layer
            <span className="block text-accent font-accent">Market Insights Platform</span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Stay ahead with the latest market data, regulatory updates, and talent trends that impact GCC establishment decisions.
            Our intelligence platform provides the insights you need for strategic planning.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Live Metrics Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-2xl p-6 sticky top-8">
              <div className="mb-6">
                <h3 className="font-headline font-headline-bold text-lg text-text-primary">
                  Market Metrics
                </h3>
                <p className="text-xs text-text-secondary mt-1">As of {DATA_AS_OF}</p>
              </div>

              <div className="space-y-6">
                {liveMetrics.map((metric, index) => (
                  <div key={index} className="border-b border-border pb-4 last:border-b-0 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">{metric.label}</span>
                      <div className={`flex items-center space-x-1 ${getTrendColor(metric.trend)}`}>
                        <Icon name={getTrendIcon(metric.trend)} size={14} />
                        <span className="text-xs font-body-medium">{metric.change}</span>
                      </div>
                    </div>
                    <div className="font-headline font-headline-bold text-xl text-text-primary">
                      {metric.value}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <Button
                  variant="outline"
                  size="default"
                  fullWidth
                  iconName="BarChart3"
                  iconPosition="left"
                  iconSize={16}
                  asChild
                >
                  <Link to="/market-intelligence">
                    View Full Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              {/* Tab Navigation */}
              <div className="border-b border-border">
                <div className="flex">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-6 py-4 text-sm font-body-medium transition-colors duration-300 ${
                        activeTab === tab.id
                          ? 'text-primary bg-primary/5 border-b-2 border-primary' : 'text-text-secondary'
                      }`}
                    >
                      <Icon name={tab.icon} size={16} />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content Area */}
              <div className="p-6">
                <div className="space-y-6">
                  {getCurrentData().map((item, index) => (
                    <div
                      key={item.id}
                      className={`border border-border rounded-lg p-6 transition-all duration-500 ${
                        index === currentUpdate ? 'bg-accent/5 border-accent/20 shadow-lg' : 'bg-background'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-body font-body-medium text-lg text-text-primary">
                              {item.title}
                            </h4>
                            {item.metric && (
                              <span className="bg-accent/10 text-accent px-2 py-1 rounded text-xs font-body-medium">
                                {item.metric}
                              </span>
                            )}
                          </div>
                          <p className="text-text-secondary leading-relaxed mb-3">
                            {item.description}
                          </p>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs border ${getImpactColor(item.impact)}`}>
                              {item.impact} Impact
                            </span>
                            <span className="text-text-secondary">
                              {item.category} • {item.source || 'Market Analysis'}
                            </span>
                          </div>
                        </div>
                        {index === currentUpdate && (
                          <div className="ml-4">
                            <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Access Full Intelligence Hub - No Demo Button */}
                <div className="mt-8 pt-6 border-t border-border text-center">
                  <Button
                    variant="default"
                    size="default"
                    className="bg-cta text-cta-foreground"
                    iconName="ExternalLink"
                    iconPosition="right"
                    iconSize={16}
                    asChild
                  >
                    <Link to="/market-intelligence">
                      Access Full Intelligence Hub
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketIntelligence;